/**
 * @file VariantOptionForm.tsx
 *
 * Add/edit form for a single product variant option (e.g. “Size” → S, M, L).
 * Values are entered as pills: type into the values box (Enter or comma commits),
 * rename a pill in place by clicking its label, remove it with its own ✕. Validates
 * that the option name is present and unique among the other options, and that at
 * least one value is provided, before reporting via `onSave`.
 *
 * A value is a `{ id?, value }` pair, and holding the id is the whole point: it is
 * what lets a save say "this existing value now reads Crimson" instead of "Red is
 * gone, here is Crimson" — the engine renames an id-bearing value in place and its
 * variants keep their prices, stock, and SKUs. Every gesture here — typing,
 * renaming, re-casing, removing — therefore preserves the id of a value it did not
 * mean to destroy; only a genuinely new value has none.
 *
 * Two gestures put text into the list — typing in the values box and renaming a
 * pill — and they resolve the same question differently: typing `red` where `Red`
 * exists re-labels that value, while *renaming* one value onto another's folded
 * label is refused (the engine rejects two values with one folded label,
 * `product_option_duplicate` 422). They share the verdict ({@link resolveLabel})
 * and not the behaviour, and both run through one merge — which is also what lets
 * Save flush an open rename and the values box in a single call, so "Enter then
 * Save" and "Save straight away" cannot disagree.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FocusEvent, KeyboardEvent, RefObject } from 'react';
import { Button, ButtonGroup, GeneralField, Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import type { ShopOptionValue } from '../../../api/shop-types';
import { MAX_VALUES_PER_OPTION } from '../product-form-constants';
import { resolveLabel } from '../product-form-utils';
import styles from './VariantOptionForm.module.css';

interface VariantOptionFormProps {
  mode: 'add' | 'edit';
  initialName?: string;
  initialValues?: ShopOptionValue[];
  /** Lower-cased names of the other options, to reject duplicates. */
  existingNames: string[];
  onSave: (name: string, values: ShopOptionValue[]) => void;
  onCancel: () => void;
}

/** Splits the values box's text into trimmed, non-empty chunks. */
function chunksFromDraft(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * How a pill is addressed while it is being renamed or removed. Deliberately not its
 * position — an index held across a removal would quietly point at a different value.
 * `||` and not `??`: an empty-string id identifies nothing, and every value carrying one
 * would answer to the single key `""`, so one ✕ would take all of them.
 */
function valueKey(value: ShopOptionValue): string {
  return value.id || value.value;
}

/** An open pill rename, applied by {@link mergeIntoValues}. */
interface EditTarget {
  index: number;
  label: string;
}

/**
 * Folds text into the committed values: an open pill rename first, then whatever the
 * values box holds. **The rename goes first, and the order is load-bearing** — a chunk
 * typed into the box has to see the label the pill now reads, not the one it is replacing.
 *
 * The two paths apply their own policy to the shared {@link resolveLabel} verdict:
 * - **A rename is positional** and folding onto a DIFFERENT value is refused (collision) —
 *   quietly re-labelling the other value would destroy something the merchant never
 *   touched. Folding onto itself is a re-casing, always allowed.
 * - **A chunk is by label** and can never collide: folding onto an existing value IS a
 *   re-labelling of it (id kept). Only an exact-label match changes nothing and is
 *   reported as a duplicate.
 *
 * Only genuinely NEW values count against `maxValues` — a rename adds nothing, so an
 * option at its cap still renames while refusing new values.
 *
 * @param values - The committed values, in display order.
 * @param chunks - The trimmed, non-empty texts from the values box.
 * @param maxValues - Cap on the value count; omit for no cap (Save reports the real total).
 * @param editTarget - An open pill rename: which value, and its new text.
 *
 * @returns The merged values (order preserved, ids intact), the chunks that changed
 *   nothing, how many new values did not fit, and any rename that was refused.
 */
function mergeIntoValues(
  values: ShopOptionValue[],
  chunks: string[],
  maxValues?: number,
  editTarget?: EditTarget,
): {
  values: ShopOptionValue[];
  duplicates: string[];
  overflowed: number;
  collisions: string[];
} {
  const merged = [...values];
  const duplicates: string[] = [];
  const collisions: string[] = [];
  let overflowed = 0;

  if (editTarget && merged[editTarget.index]) {
    const verdict = resolveLabel(merged, editTarget.label, editTarget.index);
    if (verdict.kind === 'collides' || verdict.kind === 'exact-duplicate') {
      collisions.push(editTarget.label);
    } else {
      merged[editTarget.index] = { ...merged[editTarget.index], value: editTarget.label };
    }
  }

  for (const chunk of chunks) {
    const verdict = resolveLabel(merged, chunk);
    if (verdict.kind === 'new') {
      // Take only what fits under the cap rather than failing the whole save later.
      if (maxValues !== undefined && merged.length >= maxValues) overflowed += 1;
      else merged.push({ value: chunk });
    } else if (verdict.kind === 'exact-duplicate') {
      duplicates.push(chunk);
    } else {
      merged[verdict.index] = { ...merged[verdict.index], value: chunk };
    }
  }

  return { values: merged, duplicates, overflowed, collisions };
}

/**
 * Renders the variant-option editor.
 *
 * @param props - Mode, initial values, sibling names, and save/cancel handlers.
 *
 * @returns The variant-option form.
 */
export function VariantOptionForm({
  mode,
  initialName = '',
  initialValues = [],
  existingNames,
  onSave,
  onCancel,
}: VariantOptionFormProps) {
  const intl = useIntl();
  const nameId = useId();
  const typeId = useId();
  const editErrorId = useId();
  // Save is the one control a rename may be left FOR without being abandoned, so the
  // blur handler must recognise it. By id, not ref: Gravity's Button accepts no ref but
  // spreads its remaining props onto the DOM button.
  const saveButtonId = useId();

  const [name, setName] = useState(initialName);
  const [values, setValues] = useState<ShopOptionValue[]>(initialValues);
  const [draft, setDraft] = useState('');
  const [nameError, setNameError] = useState('');
  const [valuesError, setValuesError] = useState('');

  // The pill currently open for renaming, addressed by identity rather than position,
  // plus its own error slot — a collision message belongs at the pill, not under the
  // values box where the next keystroke there would clear it.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState('');

  const valuesInputRef = useRef<HTMLInputElement>(null);
  // Every gesture that ENDS an inline edit unmounts the control the merchant stands on,
  // and focus falls to <body>. The values box is the one always-mounted control here.
  // Requested as state and applied from an effect: the handler runs BEFORE React
  // unmounts what is being left — focusing there would blur a still-mounted rename
  // field and run its blur commit a second time (on Escape, applying the very rename
  // just abandoned).
  const [focusValuesRequest, setFocusValuesRequest] = useState(0);
  const returnFocusToValues = useCallback(() => setFocusValuesRequest((n) => n + 1), []);
  useEffect(() => {
    if (focusValuesRequest === 0) return;
    valuesInputRef.current?.focus();
  }, [focusValuesRequest]);

  const editingIndex = values.findIndex((v) => valueKey(v) === editingKey);
  // An open rename with text in it. Empty means the merchant cleared the field — a
  // cancelled rename, not a request for a nameless value.
  const editTarget = useMemo<EditTarget | undefined>(() => {
    const trimmed = editDraft.trim();
    return editingIndex >= 0 && trimmed !== '' ? { index: editingIndex, label: trimmed } : undefined;
  }, [editDraft, editingIndex]);

  // Mirror the commerce-engine per-option value ceiling (>50 values is rejected by the
  // contract) so a 51st value is refused in the editor rather than at save. Renames are
  // exempt — they add nothing.
  const atValueCap = values.length >= MAX_VALUES_PER_OPTION;

  const duplicateMessage = useCallback(
    (labels: string[]) =>
      intl.formatMessage(adminMessages.formVariantValueDuplicate, {
        values: labels.join(', '),
      }),
    [intl],
  );

  const closeEdit = useCallback(() => {
    setEditingKey(null);
    setEditDraft('');
    setEditError('');
  }, []);

  const startEdit = useCallback((value: ShopOptionValue) => {
    setEditingKey(valueKey(value));
    setEditDraft(value.value);
    setEditError('');
  }, []);

  /**
   * Applies the open rename. Returns the labels it could NOT apply — empty when it
   * landed — because the two callers owe the merchant the same refusal in different
   * places: Enter holds the field open with the message at the pill, while blur lets the
   * field go and restates the message under the values box, which outlives it.
   */
  const commitEdit = useCallback((): string[] => {
    if (!editTarget) {
      closeEdit();
      return [];
    }
    const { values: merged, collisions } = mergeIntoValues(
      values,
      [],
      MAX_VALUES_PER_OPTION,
      editTarget,
    );
    if (collisions.length > 0) {
      setEditError(duplicateMessage(collisions));
      return collisions;
    }
    setValues(merged);
    closeEdit();
    return [];
  }, [closeEdit, duplicateMessage, editTarget, values]);

  const onEditKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        event.preventDefault();
        // Only a commit that landed hands focus on — a refused one keeps the field
        // open, and focus belongs where the merchant has to fix it.
        if (commitEdit().length === 0) returnFocusToValues();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeEdit();
        returnFocusToValues();
      }
    },
    [closeEdit, commitEdit, returnFocusToValues],
  );

  // Leaving the rename field commits an applicable rename and abandons an unapplicable
  // one — except when leaving directly FOR Save, which must see the rename still open so
  // its own merge applies it (or reports the collision at the pill). Save also refuses
  // focus on mousedown; the two together make the gesture behave the same whichever way
  // it is performed. A refused-and-abandoned rename is restated under the values box —
  // the pill's own error slot unmounts with the field, so a refusal written there would
  // never render. Deliberately does NOT restore focus: a blur means the merchant is
  // already standing somewhere else.
  const onEditBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>): void => {
      const movingTo = event.relatedTarget;
      if (movingTo && movingTo.id === saveButtonId) return;

      const refused = commitEdit();
      if (refused.length === 0) return;
      closeEdit();
      setValuesError(duplicateMessage(refused));
    },
    [closeEdit, commitEdit, duplicateMessage, saveButtonId],
  );

  // Removal asks no question of its own: nothing is lost until the option saves, and a
  // save that removes values raises the single confirm naming them (ProductForm's
  // value-loss modal) — a second question at the ✕ would be the same warning twice.
  const removeValue = useCallback(
    (key: string): void => {
      setValues((prev) => prev.filter((v) => valueKey(v) !== key));
      // The value being renamed may be the one just removed; either way the open
      // editor now points at nothing.
      if (editingKey === key) closeEdit();
      returnFocusToValues();
    },
    [closeEdit, editingKey, returnFocusToValues],
  );

  const commitDraft = useCallback((): void => {
    const chunks = chunksFromDraft(draft);
    if (chunks.length === 0) return;

    const { values: merged, duplicates, overflowed } = mergeIntoValues(
      values,
      chunks,
      MAX_VALUES_PER_OPTION,
    );
    setValues(merged);
    setDraft('');

    if (overflowed > 0) {
      setValuesError(
        intl.formatMessage(adminMessages.formVariantMaxValues, {
          max: MAX_VALUES_PER_OPTION,
        }),
      );
    } else if (duplicates.length > 0) {
      setValuesError(duplicateMessage(duplicates));
    } else {
      setValuesError('');
    }
  }, [draft, duplicateMessage, intl, values]);

  const onValueKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        commitDraft();
      }
    },
    [commitDraft],
  );

  const submit = useCallback((): void => {
    const trimmedName = name.trim();

    // A Save commits everything still in flight — the values box AND an open pill
    // rename — through the same merge the commit gestures use, in ONE call. That is what
    // makes "rename, Enter, Save" and "rename, Save" land identically. The cap is not
    // applied here: the count check below reports the real total rather than silently
    // dropping the overflow.
    const merged = mergeIntoValues(values, chunksFromDraft(draft), undefined, editTarget);

    // A rename the engine would reject blocks the save and says so at the pill, rather
    // than going through with the merchant's other changes and dropping this one.
    if (merged.collisions.length > 0) {
      setEditError(duplicateMessage(merged.collisions));
      return;
    }

    let hasError = false;
    if (!trimmedName) {
      setNameError(intl.formatMessage(adminMessages.formVariantNameRequired));
      hasError = true;
    } else if (existingNames.includes(trimmedName.toLowerCase())) {
      setNameError(intl.formatMessage(adminMessages.formVariantNameDuplicate));
      hasError = true;
    } else {
      setNameError('');
    }
    if (merged.values.length === 0) {
      setValuesError(intl.formatMessage(adminMessages.formVariantValuesRequired));
      hasError = true;
    } else if (merged.values.length > MAX_VALUES_PER_OPTION) {
      setValuesError(
        intl.formatMessage(adminMessages.formVariantMaxValues, {
          max: MAX_VALUES_PER_OPTION,
        }),
      );
      hasError = true;
    } else {
      setValuesError('');
    }
    if (hasError) return;

    closeEdit();
    onSave(trimmedName, merged.values);
  }, [closeEdit, draft, duplicateMessage, editTarget, existingNames, intl, name, onSave, values]);

  return (
    <div className="gv-flex gv-flex-col gv-gap-md gv-w-full">
      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={nameId}>
          <span>
            <FormattedMessage {...adminMessages.formOptionName} />
          </span>
        </label>
        <GeneralField
          id={nameId}
          value={name}
          placeholder={intl.formatMessage(adminMessages.formOptionNamePlaceholder)}
          error={nameError || undefined}
          aria-label={intl.formatMessage(adminMessages.formOptionName)}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={typeId}>
          <span>
            <FormattedMessage {...adminMessages.formOptionType} />
          </span>
        </label>
        <GeneralField
          id={typeId}
          inputRef={valuesInputRef as RefObject<HTMLInputElement>}
          value={draft}
          placeholder={intl.formatMessage(adminMessages.formOptionTypePlaceholder)}
          error={valuesError || undefined}
          aria-label={intl.formatMessage(adminMessages.formOptionType)}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onValueKeyDown}
          onBlur={commitDraft}
        />
      </div>

      {values.length > 0 && (
        <div className={styles.pills}>
          {values.map((value) => {
            const key = valueKey(value);
            if (key === editingKey) {
              return (
                // The pill becomes the field, in place and at pill size — the `ch`
                // width tracks the draft so the pill grows with the text. The draft
                // lives in its own state rather than being written through to the
                // value on every keystroke, so the pill's React key cannot change
                // underneath the input and take the focus with it.
                <span key={key} className="gv-inline-flex gv-items-center gv-gap-xs">
                  <span className={`${styles.valuePill} ${styles.valuePillEditing}`}>
                    <input
                      autoFocus
                      className={styles.valuePillInput}
                      style={{ width: `${Math.max(editDraft.length, 4) + 1}ch` }}
                      value={editDraft}
                      data-testid="product-form-value-rename"
                      aria-label={intl.formatMessage(adminMessages.formVariantValueRename, {
                        value: value.value,
                      })}
                      aria-describedby={editError ? editErrorId : undefined}
                      onChange={(event) => {
                        setEditDraft(event.target.value);
                        setEditError('');
                      }}
                      onBlur={onEditBlur}
                      onKeyDown={onEditKeyDown}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </span>
                  {editError && (
                    // Beside its own pill, so it wraps with the value it is about. A
                    // live region as well as a description target: it appears while
                    // focus stays put in an unchanged field, so without the role a
                    // screen reader announces nothing.
                    <span id={editErrorId} role="alert" className="gv-text-sm gv-input-error-text">
                      {editError}
                    </span>
                  )}
                </span>
              );
            }
            return (
              // One pill, two controls within it: the label opens the rename editor
              // and the ✕ removes. Not Gravity's Chip, which is a single <button> a
              // second control cannot legally nest inside — the pill is a <span> drawn
              // from the same tokens, so both controls keep native button semantics.
              <span
                key={key}
                className={styles.valuePill}
                data-testid="product-form-value-pill"
                data-value={value.value}
              >
                <button
                  type="button"
                  className={styles.valuePillLabel}
                  data-testid="product-form-value-label"
                  aria-label={intl.formatMessage(adminMessages.formVariantValueRename, {
                    value: value.value,
                  })}
                  onClick={() => startEdit(value)}
                >
                  {value.value}
                </button>
                <button
                  type="button"
                  className={styles.valuePillRemove}
                  data-testid="product-form-value-remove"
                  aria-label={intl.formatMessage(adminMessages.formVariantValueRemove, {
                    value: value.value,
                  })}
                  onClick={() => removeValue(key)}
                >
                  <Icon name="close" size="small" aria-hidden />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {atValueCap && (
        <p
          className="gv-text-sm gv-state-critical"
          role="alert"
          data-testid="product-form-value-cap"
        >
          <FormattedMessage
            {...adminMessages.formVariantMaxValues}
            values={{ max: MAX_VALUES_PER_OPTION }}
          />
        </p>
      )}

      <ButtonGroup className="gv-flex-col-reverse gv-tab-flex-row">
        <Button type="button" variant="cancel" condensed onClick={onCancel}>
          <FormattedMessage {...adminMessages.formCancel} />
        </Button>
        <Button
          id={saveButtonId}
          type="button"
          variant="primary"
          condensed
          // A pointer press must not take the focus off an open rename: the blur it
          // causes would abandon a rename the engine would reject and wipe the error
          // explaining why in the same batch. Suppressing the default on mousedown
          // suppresses only the focus transfer; the click still fires, the open rename
          // is still there for the merge, and a collision is reported at its pill.
          onMouseDown={(event) => event.preventDefault()}
          onClick={submit}
        >
          <FormattedMessage {...adminMessages.formSave} />
        </Button>
      </ButtonGroup>
    </div>
  );
}
